// Browser page translation (Chrome/Google Translate, Edge, Safari) rewrites
// text nodes in place: it wraps them in <font> elements and moves them. React
// still holds references to the ORIGINAL nodes, so its next removeChild or
// insertBefore targets a node whose parent is no longer the one React thinks,
// and the DOM throws ("Failed to execute 'removeChild' on 'Node'", Safari's
// "The object can not be found here"). That unmounts the route to the error
// fallback. The TikTok spike (2026-09-26) sent translate-crash clients from
// ~15/day to 135/day, nearly all Spanish speakers translating the page.
//
// We do NOT set translate="no": translation is how those users read the app.
// Instead we make the two calls tolerant, the workaround from
// facebook/react#11538. When the child is no longer where React expects, the
// call becomes a no-op (removeChild), or inserts before the translation
// wrapper that now holds the reference node (insertBefore), which keeps the
// sibling order React intended.

import { trackEvent } from './analytics'

let installed = false
let reported = false

function reportOnce(op: string) {
  if (reported) return
  reported = true
  // One event per page load, so we can see the guard firing without a flood.
  // Telemetry must never turn the guard back into a throw.
  try {
    trackEvent('translate_dom_guard', { op })
  } catch {
    // ignore
  }
}

export function installTranslateGuard(): void {
  if (installed || typeof Node === 'undefined' || !Node.prototype) return
  installed = true

  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // eslint-disable-next-line i18next/no-literal-string -- DOM method name
      reportOnce('removeChild')
      return child
    }
    return originalRemoveChild.call(this, child) as T
  }

  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // eslint-disable-next-line i18next/no-literal-string -- DOM method name
      reportOnce('insertBefore')
      // Translation wrapped the reference node; insert before the wrapper
      // (its ancestor that is our direct child) to keep sibling order.
      let anchor: Node = referenceNode
      while (anchor.parentNode && anchor.parentNode !== this) {
        anchor = anchor.parentNode
      }
      if (anchor.parentNode === this) {
        return originalInsertBefore.call(this, newNode, anchor) as T
      }
      // The reference is not under us at all: inserting anywhere would be a
      // guess, so do nothing rather than misplace the node.
      return newNode
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}
